import classNames from 'classnames';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getMyAccountMenuItems } from '../../constants/menuItems';
import { useGetAllCards } from '../../hooks/apolloHooks/useGetAllCards';
import { useGetCustomer } from '../../hooks/apolloHooks/useGetCustomer';
import { GET_BANKCARDS_CUSTOMER_DATA_QUERY, GET_BASIC_CUSTOMER_DATA_QUERY, GET_CONTACT_CUSTOMER_DATA_QUERY, GET_CUSTOMER_AGREEMENTS_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries';
import { BankCardsCustomerDataResponse, BasicCustomerDataResponse, ContactDataResponse, CustomerAgreementsResponse } from '../../integrations/allkort/graphql/interface/Customer';
import ErrorDisplay from '../elements/ErrorDisplay';
import FetchingAnimation from '../elements/FetchingAnimation';
import ProfileCreditCard from '../elements/profilesettings/ProfileCreditCard';
import ProfileDeleteAccount from '../elements/profilesettings/ProfileDeleteAccount';
import ProfileDetails from '../elements/profilesettings/ProfileDetails';
import ProfileDigitalReceipts from '../elements/profilesettings/ProfileDigitalReceipts';
import ProfileSmartCard from '../elements/profilesettings/ProfileSmartCard';
import ProfileVehicleSpecification from '../elements/profilesettings/ProfileVehicleSpecification';
import SecondMenu from '../elements/SecondMenu';
type ProfileSettingsProps = { data?: any }

const ProfileSettingsPage: React.FC<ProfileSettingsProps> = () => {
	const settingsPageWrapperClasses = 'white-pale-section shadow-one';
	const { hash } = useLocation();

	// Scroll to hash anchor when navigating directly to a section (e.g. #remove-account)
	useEffect(() => {
		if (!hash) return;
		const id = hash.replace('#', '');
		const scrollTimeout = setTimeout(() => {
			const el = document.getElementById(id);
			if (el) {
				const top = el.getBoundingClientRect().top + window.pageYOffset - 170;
				window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
			}
		}, 500);
		return () => clearTimeout(scrollTimeout);
	}, [hash]);

	const { cards, isComplete } = useGetAllCards()
	const cardTypes = new Set(cards.map(card => card.type.shortName))

	// Fetch bank cards data with same pagination as regular cards
	const bankCardsVariables = React.useMemo(() => ({ first: null, after: null, before: null, last: 49 }), []);

	const { data: bankCardsData, loading: bankCardsLoading, error: bankCardsError } = useGetCustomer<BankCardsCustomerDataResponse>(
		GET_BANKCARDS_CUSTOMER_DATA_QUERY,
		bankCardsVariables
	);

	const hasBankCards = (bankCardsData?.currentCustomer?.bankCards?.nodes?.length ?? 0) > 0;

	// Check which card types customer has which is synonym to if they have invoice and statements services
	let hasP = cardTypes.has('P')  // Smartkort
	let hasK = cardTypes.has('K')  // Tankkort/Företagskort
	const menuItems = getMyAccountMenuItems(hasP, hasK);

	// Always fetch data - no props needed
	const { data: customerBasicData, loading: customerLoading, error: customerError } = useGetCustomer<BasicCustomerDataResponse>(
		GET_BASIC_CUSTOMER_DATA_QUERY,
		undefined,
		{ fetchPolicy: 'network-only' }
	);

	const { data: customerContactData, loading: customerContactLoading, error: customerContactError, refetch: refetchContactData } = useGetCustomer<ContactDataResponse>(
		GET_CONTACT_CUSTOMER_DATA_QUERY,
		undefined,
		{ fetchPolicy: 'network-only' }
	);

	const BATCH_SIZE = 15; // Number of agreements to fetch per request
	const { data: agreementsData, loading: agreementsLoading, error: agreementsError } = useGetCustomer<CustomerAgreementsResponse>(
		GET_CUSTOMER_AGREEMENTS_QUERY,
		{ first: BATCH_SIZE, after: null, before: null, last: null }
	);

	// Console log errors// Console log errors
	if (agreementsError) console.error('Failed to load agreements:', agreementsError.message)
	if (customerError) console.error('Failed to load customer data:', customerError.message)
	if (customerContactError) console.error('Failed to load customer contact data:', customerContactError.message)
	if (bankCardsError) console.error('Failed to load bank cards:', bankCardsError.message)

	const hasAppAgreement = agreementsData?.currentCustomer?.agreementApprovals?.nodes?.some(
		approval => approval.agreement.id === 354 // 354 is the id for the agreement related to the app agreement, this is hardcoded until we have a better way to identify it
	) ?? false;

	const isCompany = customerBasicData?.currentCustomer?.isCompany || false;
	const isFrozen = customerBasicData?.currentCustomer?.frozen.is || false;

	/* Skeleton loading animation TODO: adjust this one to fit better */
	if (customerLoading || customerContactLoading || !isComplete) {
		return (
			<div className="skeleton-settings-page">
				{/* this anumates the secondmenu component */}
				<FetchingAnimation width={644} maxWidth={true} height={151} marginBottom={14} marginTop={0} />
				<div className="skeleton-page-content-wrapper">
					<FetchingAnimation width={'100'} maxWidth={true} height={240} marginBottom={10} marginTop={15} />
					<FetchingAnimation width={'100'} maxWidth={true} height={700} marginBottom={10} marginTop={32} />
				</div>
			</div>);
	}

	if (agreementsError || customerError || customerContactError || bankCardsError) {
		return (
			<>
				{agreementsError && <ErrorDisplay errorContext="Fel vid laddning av avtal:" errorMessage={agreementsError.message} />}
				{customerError && <ErrorDisplay errorContext="Fel vid laddning av kunddata:" errorMessage={customerError.message} />}
				{customerContactError && <ErrorDisplay errorContext="Fel vid laddning av kontaktuppgifter:" errorMessage={customerContactError.message} />}
				{bankCardsError && <ErrorDisplay errorContext="Fel vid laddning av bankkort:" errorMessage={bankCardsError.message} />}
			</>
		);
	}

	return (
		<>
			<SecondMenu menuItems={menuItems} />
			<div className="Section profile-settings-page-component">
				<div className="Row Row--JustifyLeft">
					{/* Header h1 and info message */}
					<div
						className={classNames(
							'Row__Inner',
							'profile-settings-header',
						)}

					>
						<h1>Inställningar</h1>
						<p>
							Här kan du se din personliga uppgifter, hantera dina kontaktuppgifter samt inställningar.
						</p>
					</div>

					{/* Content sections: profile details */}
					<ProfileDetails
						customerData={customerBasicData!}
						customerContactData={customerContactData!}
						refetchContactData={refetchContactData}
					/>

					<div className={classNames(
						'profile-settings-cards-wrapper',
						'Row__Inner',
						{
							'has-both-cards': hasP && hasK,
							'has-one-card': (hasP && !hasK) || (!hasP && hasK),
							'has-no-cards': !hasP && !hasK
						}
					)
					}>
						{hasK && (
							<ProfileCreditCard
								isCompany={isCompany}
								customerContactData={customerContactData!}
								refetchContactData={refetchContactData}
							/>
						)}

						{hasP && (
							<ProfileSmartCard
								customerContactData={customerContactData!}
								refetchContactData={refetchContactData}
							/>
						)}
					</div>


					{(hasP || hasK || hasBankCards || hasAppAgreement) && (
						<div className={classNames(
							'profile-settings-other-services-wrapper',
							'Row__Inner',
							{
								'has-vehicle-specification': (hasK && isCompany),
								'no-vehicle-specification': !isCompany
							}
						)
						}>
							<ProfileDigitalReceipts
								customerContactData={customerContactData!}
								customerId={customerBasicData?.currentCustomer?.id ?? 0}
								refetchContactData={refetchContactData}
							/>
							{(isCompany && hasK) && (
								<ProfileVehicleSpecification
									customerContactData={customerContactData!}
									customerId={customerBasicData?.currentCustomer?.id ?? 0}
									refetchContactData={refetchContactData}
								/>
							)}

						</div>
					)}
					{/* Does not check right now if there is a task with remove account already */}
					{!isFrozen && (
						<div
							className={classNames(
								'profile-settings-delete-account-wrapper',
								'Row__Inner',
							)
							}
							id={'remove-account'}

						>
							{/* TODO: if you have one task already show other text. */}
							<ProfileDeleteAccount
								hasP={hasP}
								hasK={hasK}
								hasBankCards={hasBankCards}
								hasAppAgreement={hasAppAgreement}
								isCompany={isCompany}
								customerContactData={customerContactData!}
							/>
						</div>
					)}

				</div>
			</div>
		</>
	)
}
export default ProfileSettingsPage
